import { useState } from 'react';
import { Select, SelectItem, Switch } from '@scholastic/mockingjay';
import { LoadingSelect } from '@data/components/basic/LoadingSelect/LoadingSelect';
import { LabeledMultiSelect } from '@data/components/report/ReportFilterBar/LabeledMultiSelect/LabeledMultiSelect';
import { useOrgOptions } from '@data/hooks/use-org-label/use-org-label';

export const ALL_ORGANIZATIONS_LABEL = 'All Organizations';

/** Marks an organization the product does not reach. */
const NO_ACCESS_TAG = 'No access';

const HAS_ACCESS_ONLY_LABEL = 'Has access only';

function NoAccessTag() {
  return (
    <span className="rounded-sm bg-neutral-100 px-1 py-0.5 text-xs font-bold leading-normal text-neutral-900">
      {NO_ACCESS_TAG}
    </span>
  );
}

/**
 * Narrows the list to the organizations that hold the product.
 *
 * It sits inside the menu but is not one of its options, so its events stop
 * here rather than reaching the select's own typeahead and selection.
 */
function HasAccessOnlySwitch({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center px-[9px] py-1 [&_label]:pr-3 [&_label]:text-xs [&_label]:font-bold [&_label]:leading-normal [&_label]:text-neutral-1300">
      <Switch
        label={HAS_ACCESS_ONLY_LABEL}
        checked={checked}
        onCheckedChange={onChange}
        onKeyDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      />
    </div>
  );
}

interface OrganizationDropdownProps {
  /** Empty means every organization, as an empty class selection does. */
  selectedOrgIds: string[];
  onChange: (orgIds: string[]) => void;
  /**
   * `single` for a report about one organization. With only one to choose
   * from the control is dropped — there is nothing to pick.
   */
  mode?: 'multiselect' | 'single';
  /** Fixed to the organization being read, with no choice to make. */
  disabled?: boolean;
  /**
   * The product the report is about. The list still holds every organization;
   * this only decides which of them are marked as lacking the product.
   */
  appCode?: string;
}

/**
 * The organizations an admin may scope a report to.
 *
 * The same list at every level — tags, switch and typeahead included — so that
 * drilling in changes how many may be picked and nothing else. Deliberately
 * unscoped by product, so switching product never drops a selection out.
 */
export function OrganizationDropdown({
  selectedOrgIds,
  onChange,
  mode = 'multiselect',
  disabled = false,
  appCode,
}: OrganizationDropdownProps) {
  const { data: orgOptions, isLoading } = useOrgOptions(appCode);
  // A way to read the list, not part of the report's scope, so it stays out of
  // the URL and resets with the page.
  const [hasAccessOnly, setHasAccessOnly] = useState(false);

  if (isLoading) {
    return (
      <div className="w-full tablet:w-[280px]">
        <LoadingSelect />
      </div>
    );
  }

  if (mode === 'single' && orgOptions.length <= 1) {
    // One organization is nothing to choose between.
    return null;
  }

  if (mode === 'single') {
    // The switch narrows the list; it never narrows what is already chosen.
    const listed =
      hasAccessOnly && !disabled
        ? orgOptions.filter(
            (org) => org.hasAccess || selectedOrgIds.includes(org.orgId)
          )
        : orgOptions;

    return (
      <div className="w-full tablet:w-[280px]">
        <div className="flex w-full [&>div]:w-full [&_button]:min-w-0">
          <Select
            label="Organization"
            value={selectedOrgIds[0] ?? ''}
            disabled={disabled}
            onValueChange={(orgId: string) => onChange([orgId])}
          >
            {appCode && !disabled && (
              <HasAccessOnlySwitch
                checked={hasAccessOnly}
                onChange={setHasAccessOnly}
              />
            )}
            {listed.map((org) => (
              <SelectItem
                key={org.orgId}
                value={org.orgId}
                {...(org.hasAccess ? {} : { tag: <NoAccessTag /> })}
              >
                {org.orgName}
              </SelectItem>
            ))}
          </Select>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full tablet:w-[280px]">
      <LabeledMultiSelect
        label="Organization"
        inputId="organization-multiselect"
        selectAllLabel={ALL_ORGANIZATIONS_LABEL}
        options={orgOptions.map((org) => ({
          value: org.orgId,
          label: org.orgName,
          ...(org.hasAccess ? {} : { tag: NO_ACCESS_TAG }),
        }))}
        selectedIds={selectedOrgIds}
        onChange={onChange}
        {...(appCode
          ? {
              filterSwitch: {
                label: HAS_ACCESS_ONLY_LABEL,
                checked: hasAccessOnly,
                onChange: setHasAccessOnly,
                matches: (option) => option.tag !== NO_ACCESS_TAG,
              },
            }
          : {})}
      />
    </div>
  );
}
